<template>
  <div
    ref="scrollContainerRef"
    class="llm-tips-widget-view"
    :style="style"
  >
    <div
      class="widget-wrapper"
      :style="{ marginLeft: widget.marginLeft + 'px' }"
    >
      <div
        class="horizontal-scroll-container"
        :class="{ 'no-data': !hasRealData }"
        :style="{ gap: widget.gap + 'px' }"
      >
        <div
          v-for="(tip, index) in displayData"
          :key="index"
          class="tip-card"
          :style="{
            color: widget.fontColor,
            boxShadow: widget.boxShadow
          }"
        >
          <div class="tip-number">
            {{ index + 1 }}
          </div>
          <div class="tip-text">
            {{ tip.text }}
          </div>
          <div class="tip-saving">
            <div class="saving-label">
              Potential saving
            </div>
            <div
              class="saving-amount"
              :style="{ color: widget.amountFontColor }"
            >
              £{{ numberToHumanReadable(tip.amount) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay box when no real data -->
    <div
      v-if="!hasRealData"
      class="no-data-overlay"
    >
      <div class="overlay-content">
        <div class="overlay-text">
          We don't have enough data to display your Saving tips.
          <span
            class="connect-link"
            @click="handleConnectAccountsClick"
          >connect an active account</span>
          to get the full picture
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LlmTipsWidgetView',
  props: {
    widget: {
      type: Object,
    },
  },
  data() {
    return {
      isScrolling: false,
      startX: 0,
      scrollLeft: 0,
    };
  },
  computed: {
    style() {
      return {
        top: `${this.widget.top}px`,
        zIndex: this.widget.zIndex,
        fontFamily: this.widget.fontFamily,
      };
    },
    hasRealData() {
      const data = this.widget.variableData;
      return data && Array.isArray(data) && data.length > 0;
    },
    displayData() {
      if (this.hasRealData) {
        return this.widget.variableData;
      }
      // Return default placeholder data when no real data
      return [
        { text: 'Connect another account to get this saving', amount: 0 },
        { text: 'Connect another account to get this saving', amount: 0 },
      ];
    },
    variableData() {
      // Keep this for backward compatibility if needed elsewhere
      return this.displayData;
    },
  },
  mounted() {
    this.setupDragScroll();
  },
  methods: {
    getVariableValue(variableName) {
      // This is a placeholder - you would need to implement this based on
      // how your application accesses dynamic variables
      if (!variableName) { return null; }

      // Check if the variable exists in window scope or other scopes as needed
      if (window[variableName]) {
        return window[variableName];
      }

      // If you have a variables store, you might access it like this:
      // return this.$store.getters.getVariable(variableName);

      return null;
    },
    handleConnectAccountsClick() {
      this.$emit('userInteraction', {
        eventName: 'connectAccountsClicked',
      });
    },
    setupDragScroll() {
      const scrollContainer = this.$refs.scrollContainerRef;
      if (!scrollContainer) { return; }

      scrollContainer.addEventListener('mousedown', e => {
        // Don't allow scrolling when there's no real data
        if (!this.hasRealData) { return; }

        this.isScrolling = true;
        this.startX = e.pageX - scrollContainer.offsetLeft;
        this.scrollLeft = scrollContainer.scrollLeft;
        scrollContainer.style.cursor = 'grabbing';
      });

      scrollContainer.addEventListener('mouseleave', () => {
        this.isScrolling = false;
        if (this.hasRealData) {
          scrollContainer.style.cursor = 'grab';
        } else {
          scrollContainer.style.cursor = 'default';
        }
      });

      scrollContainer.addEventListener('mouseup', () => {
        this.isScrolling = false;
        if (this.hasRealData) {
          scrollContainer.style.cursor = 'grab';
        } else {
          scrollContainer.style.cursor = 'default';
        }
      });

      scrollContainer.addEventListener('mousemove', e => {
        if (!this.isScrolling || !this.hasRealData) { return; }
        e.preventDefault();
        const x = e.pageX - scrollContainer.offsetLeft;
        const walk = (x - this.startX) * 2; // Scroll speed multiplier
        scrollContainer.scrollLeft = this.scrollLeft - walk;
      });
    },
    numberToHumanReadable(num) {
      if (typeof num !== 'number') { return num; }

      return num.toLocaleString('en-GB', {
        maximumFractionDigits: 0,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.llm-tips-widget-view {
  position: absolute;
  display: flex;
  width: 430px;
  left: 0;
  overflow-x: auto;
  padding: 10px 0;
  -webkit-overflow-scrolling: touch; /* For smooth scrolling on iOS */
  scrollbar-width: none; /* Hide scrollbar for Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome/Safari */
  }
  white-space: nowrap;
  cursor: grab; /* Show grab cursor to indicate draggable */

  .widget-wrapper {
    width: 100%;
    margin-right: 30px;
  }
}

.horizontal-scroll-container {
  display: flex;
  padding: 5px;
  width: 100%;

  &.no-data {
    opacity: 0.4; /* Fade only the cards when no real data */
    pointer-events: none; /* Disable all interactions */
  }
}

.tip-card {
  flex: 0 0 250px;
  min-width: 250px;
  max-width: 250px;
  background-color: white;
  border-radius: 15px;
  padding: 20px;
  min-height: 280px; /* Minimum height, but can grow */
  display: flex;
  flex-direction: column;
  box-sizing: border-box; /* Include padding in width/height calculations */
}

.tip-number {
  width: 36px;
  height: 36px;
  background-color: #FFCE00;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 22px;
  font-weight: bold;
  color: #205441;
  margin-bottom: 20px;
}

.tip-text {
  font-size: 18px;
  line-height: 1.3;
  flex: 1;
  width: 100%;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  margin-bottom: 20px; /* Ensure spacing between text and saving section */
}

.tip-saving {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto; /* Push to bottom of card */
  flex-shrink: 0; /* Prevent shrinking */
}

.saving-label {
  font-size: 16px;
  font-weight: bold;
}

.saving-amount {
  font-size: 16px;
  font-weight: bold;
}

.no-data-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  pointer-events: none; /* Allow clicks to pass through */

  .connect-link {
    pointer-events: auto; /* Enable clicks on the connect link */
  }
}

.overlay-content {
  background: linear-gradient(135deg, #F5D547 0%, #FFCE00 100%);
  border-radius: 20px;
  padding: 20px 25px;
  width: 360px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.overlay-text {
  color: #1a365d;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.connect-link {
  color: #2563eb;
  text-decoration: underline;
  font-weight: 700;
  cursor: pointer;
}
</style>
